import { useEffect, useState } from "react"

import { useLazyGetInstalledDevicesWidgetQuery } from "store/api/adminApi"
import { useLazyGetInstalledDevicesQuery } from "store/api/widgetsApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"

import { getDateRangeFromOption } from "utils/dateUtils"
import { HalfDoughnutLegendGrid, HalfDoughnutSummaryWidget } from "components/widgets/HalfDoughnutSummaryWidget"
import { allDateSelectOptions } from "pages/vendor/Dashboard/constants"

import { buildInstalledDeviceItems } from "./items"

const initialWidgetData = {
  onlineV1: 0,
  offlineV1: 0,
  onlineV2: 0,
  offlineV2: 0,
}

interface Props {
  isVendor?: boolean
}

export const WidgetInstaledDevices = ({ isVendor = false }: Props) => {
  const [selectedDateRange, setSelectedDateRange] = useState(() => getDateRangeFromOption("all"))
  const [triggerVendorInstalledDevices, { isFetching: isVendorFetching, data: vendorData }] =
    useLazyGetInstalledDevicesWidgetQuery()
  const [triggerContractorInstalledDevices, { isFetching: isContractorFetching, data: contractorData }] =
    useLazyGetInstalledDevicesQuery()
  const [widgetData, setWidgetData] = useState(initialWidgetData)
  const thermv2ordering = isVendor || useAppSelector(isFeatureEnabled(FeatureFlag.ThermV2Ordering))

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isVendor) {
          await triggerVendorInstalledDevices()
        } else {
          await triggerContractorInstalledDevices({
            date_start: selectedDateRange[0],
            date_end: selectedDateRange[1],
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [isVendor, selectedDateRange, triggerVendorInstalledDevices, triggerContractorInstalledDevices])

  useEffect(() => {
    const d = isVendor ? vendorData?.data : contractorData?.data
    if (!d) return

    setWidgetData({
      onlineV1: d.online_v1 ?? 0,
      offlineV1: d.offline_v1 ?? 0,
      onlineV2: d.online_v2 ?? 0,
      offlineV2: d.offline_v2 ?? 0,
    })
  }, [isVendor, vendorData, contractorData])

  const items = buildInstalledDeviceItems(thermv2ordering, widgetData)

  const total = items.reduce((sum, item) => sum + item.value, 0)

  return (
    <HalfDoughnutSummaryWidget
      title="Installed Thermostats"
      mainLabel="Thermostats"
      items={items}
      chartOptions={{ cutout: "65%" }}
      showPercentage
      legendContent={thermv2ordering ? <HalfDoughnutLegendGrid items={items} total={total} /> : undefined}
      isFetching={isVendor ? isVendorFetching : isContractorFetching}
      dateSelectOptions={!isVendor ? allDateSelectOptions : undefined}
      onSelectDateRange={!isVendor ? setSelectedDateRange : undefined}
      defaultSelectedDateOption={!isVendor ? allDateSelectOptions[0] : undefined}
    />
  )
}
